/**
 * MODULE: platform/browser/frontend/components/BrowserViewport.tsx
 * PURPOSE: Live browser stream iframe, URL bar, and session controls.
 * DEPENDENCIES: BrowserSessionStatus, BrowserEmptyState, browserPageUrl
 * PUBLIC_API: BrowserViewport
 * SIDE_EFFECTS: window.open for live view; clipboard copy
 * AGENT_SAFETY: SAFE
 * RECENT: 2026-06-18 — Editable URL bar navigates remote session via warmup API (Enter / Go).
 * RECENT: 2026-06-18 [2.2.1445] — Disable 10s auto screencast fallback; manual toggle only (WebRTC Session Player primary on Azure).
 * RECENT: 2026-06-17 [2.2.1437] — Auto screencast fallback when Session Player stalls; mode toggle banner.
 *   [2.2.1431] — Client-side navbar=false for bordered screencast when API returns bare inspector URL.
 *   [2.2.1430] — Iframe remount when live URL upgrades from legacy Steel `/ui` embed.
 *   [2.2.1429] — Trusted same-origin Steel screencast embed (no sandbox; avoids nested /ui shell).
 */

import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import {
  ArrowTopRightOnSquareIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentIcon,
} from '@heroicons/react/24/outline';
import type { BrowserMission } from '../data/browserMissions';
import type { NavigateSessionResult } from '../hooks/useBrowserSession';
import type { BrowserSession } from '../types/browser.types';
import {
  isFullscreenDevToolsLiveUrl,
  isSteelSessionPlayerUrl,
  pickEmbedLiveViewUrl,
  pickFullscreenLiveViewUrl,
  resolveEmbedLiveViewUrl,
  withInspectorEmbedParams,
} from '../utils/browserLiveViewUrl';
import { ASSISTANT_RAIL_BG_CLASS } from '@design-system/components/AiAssistant/assistantPageTheme';
import { isMeaningfulBrowserUrl } from '../utils/browserPageUrl';
import { BrowserEmptyState } from './BrowserEmptyState';
import { BrowserSessionStatus } from './BrowserSessionStatus';

interface BrowserViewportProps {
  session: BrowserSession | null;
  isConnecting: boolean;
  signInRequired?: boolean;
  sessionExpired?: boolean;
  actionCount?: number;
  lastKnownUrl?: string;
  commandPanelOpen?: boolean;
  onToggleCommandPanel?: () => void;
  onStart: () => void;
  onStop: () => void;
  onReconnect?: () => void;
  onStartMission?: (mission: BrowserMission) => void;
  showMobileMissionPicker?: boolean;
  onLiveViewDisconnected?: () => void;
  provider?: 'steel' | 'browserbase' | 'unknown';
  providerReady?: boolean;
  providerLabel?: string;
  onNavigate?: (url: string) => Promise<NavigateSessionResult>;
}

export function BrowserViewport({
  session,
  isConnecting,
  signInRequired = false,
  sessionExpired = false,
  actionCount,
  lastKnownUrl,
  commandPanelOpen = true,
  onToggleCommandPanel,
  onStart,
  onStop,
  onReconnect,
  onStartMission,
  showMobileMissionPicker = false,
  onLiveViewDisconnected,
  provider,
  providerReady = false,
  providerLabel,
  onNavigate,
}: BrowserViewportProps) {
  const urlBarValue = session?.currentUrl ?? lastKnownUrl ?? '';
  const canEditUrlBar = Boolean(session && onNavigate && !sessionExpired);
  const [urlDraft, setUrlDraft] = useState(urlBarValue);
  const [isNavigating, setIsNavigating] = useState(false);
  const [navigateError, setNavigateError] = useState<string | null>(null);
  const urlEditingRef = useRef(false);
  const playerEmbedUrl = pickEmbedLiveViewUrl(
    session?.liveViewUrl,
    session?.liveViewFullscreenUrl,
  );
  const inspectorFallbackUrl = withInspectorEmbedParams(session?.liveViewFallbackUrl ?? '');
  const [embedMode, setEmbedMode] = useState<'auto' | 'player' | 'inspector'>('auto');

  useEffect(() => {
    setEmbedMode('auto');
  }, [session?.id, playerEmbedUrl]);

  const embedLiveViewUrl = resolveEmbedLiveViewUrl(
    session?.liveViewUrl,
    session?.liveViewFullscreenUrl,
    session?.liveViewFallbackUrl,
    embedMode === 'auto' ? 'player' : embedMode,
  );
  const showEmbedModeToggle =
    Boolean(playerEmbedUrl) &&
    Boolean(inspectorFallbackUrl) &&
    isSteelSessionPlayerUrl(playerEmbedUrl);
  const fullscreenLiveViewUrl = pickFullscreenLiveViewUrl(
    session?.liveViewUrl,
    session?.liveViewFullscreenUrl,
  );
  const [urlCopied, setUrlCopied] = useState(false);
  const [preparingLong, setPreparingLong] = useState(false);

  useEffect(() => {
    if (urlEditingRef.current) return;
    setUrlDraft(urlBarValue);
  }, [urlBarValue]);

  const submitUrlNavigation = useCallback(async () => {
    if (!canEditUrlBar || !onNavigate || isNavigating) return;
    const target = urlDraft.trim();
    if (!target) return;
    setNavigateError(null);
    setIsNavigating(true);
    try {
      const result = await onNavigate(target);
      if (!result.ok) {
        setNavigateError(result.error);
        return;
      }
      setUrlDraft(result.url);
    } finally {
      setIsNavigating(false);
      urlEditingRef.current = false;
    }
  }, [canEditUrlBar, isNavigating, onNavigate, urlDraft]);

  const handleUrlKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        void submitUrlNavigation();
      }
      if (event.key === 'Escape') {
        urlEditingRef.current = false;
        setUrlDraft(urlBarValue);
        setNavigateError(null);
        event.currentTarget.blur();
      }
    },
    [submitUrlNavigation, urlBarValue],
  );

  useEffect(() => {
    if (!session || embedLiveViewUrl || isConnecting) {
      setPreparingLong(false);
      return;
    }
    const delayMs = provider === 'steel' ? 5000 : 8000;
    const timer = window.setTimeout(() => setPreparingLong(true), delayMs);
    return () => window.clearTimeout(timer);
  }, [session, embedLiveViewUrl, isConnecting, provider]);

  useEffect(() => {
    if (!onLiveViewDisconnected) return;
    const onMessage = (event: MessageEvent) => {
      if (event.data === 'browserbase-disconnected') {
        onLiveViewDisconnected();
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [onLiveViewDisconnected]);

  const handleCopyUrl = useCallback(async () => {
    if (!isMeaningfulBrowserUrl(urlBarValue)) return;
    try {
      await navigator.clipboard.writeText(urlBarValue.trim());
      setUrlCopied(true);
      window.setTimeout(() => setUrlCopied(false), 2000);
    } catch {
      // Clipboard may be unavailable in embedded contexts.
    }
  }, [urlBarValue]);

  const handleOpenLiveView = useCallback(() => {
    const liveUrl = fullscreenLiveViewUrl;
    if (!liveUrl) return;
    window.open(liveUrl, '_blank', 'noopener,noreferrer');
  }, [fullscreenLiveViewUrl]);

  const showLiveViewAction = Boolean(fullscreenLiveViewUrl);
  const showUrlActions = isMeaningfulBrowserUrl(urlBarValue);
  const urlPlaceholder = sessionExpired
    ? 'Session expired'
    : session
      ? canEditUrlBar
        ? 'Enter URL and press Enter'
        : 'Current page URL'
      : isConnecting
        ? 'Connecting…'
        : 'No page yet';

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="flex items-center gap-3 border-b border-gray-200 bg-white px-4 py-2 dark:border-gray-700 dark:bg-gray-900">
        <BrowserSessionStatus
          session={session}
          isConnecting={isConnecting}
          sessionExpired={sessionExpired}
          actionCount={actionCount}
          provider={provider}
        />
        <input
          className="min-w-0 flex-1 rounded-md border border-gray-300 bg-gray-50 px-3 py-1.5 text-sm text-gray-800 read-only:cursor-default dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
          value={canEditUrlBar ? urlDraft : urlBarValue}
          readOnly={!canEditUrlBar}
          disabled={isNavigating}
          placeholder={urlPlaceholder}
          aria-label={canEditUrlBar ? 'Navigate to URL' : 'Current URL'}
          aria-invalid={navigateError ? true : undefined}
          data-testid="browser-url-bar"
          onChange={
            canEditUrlBar
              ? (event) => {
                  urlEditingRef.current = true;
                  setNavigateError(null);
                  setUrlDraft(event.target.value);
                }
              : undefined
          }
          onFocus={
            canEditUrlBar
              ? () => {
                  urlEditingRef.current = true;
                }
              : undefined
          }
          onBlur={
            canEditUrlBar
              ? () => {
                  urlEditingRef.current = false;
                }
              : undefined
          }
          onKeyDown={canEditUrlBar ? handleUrlKeyDown : undefined}
        />
        {canEditUrlBar ? (
          <button
            type="button"
            onClick={() => void submitUrlNavigation()}
            disabled={isNavigating || !urlDraft.trim()}
            className="rounded-md border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
            data-testid="browser-url-go"
          >
            {isNavigating ? '…' : 'Go'}
          </button>
        ) : null}
        {showUrlActions ? (
          <button
            type="button"
            onClick={() => void handleCopyUrl()}
            className="rounded-md border border-gray-300 p-1.5 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
            aria-label="Copy current URL"
            title="Copy URL"
            data-testid="browser-copy-url"
          >
            {urlCopied ? (
              <ClipboardDocumentCheckIcon className="h-4 w-4" aria-hidden />
            ) : (
              <ClipboardDocumentIcon className="h-4 w-4" aria-hidden />
            )}
          </button>
        ) : null}
        {showLiveViewAction ? (
          <button
            type="button"
            onClick={handleOpenLiveView}
            className="rounded-md border border-gray-300 p-1.5 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
            aria-label="Open live browser view in a new tab"
            title="Open live view in new tab"
            data-testid="browser-open-live-view"
          >
            <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden />
          </button>
        ) : null}
        {onToggleCommandPanel && embedLiveViewUrl ? (
          <button
            type="button"
            onClick={onToggleCommandPanel}
            className="rounded-md border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
            data-testid="browser-toggle-command-panel"
          >
            {commandPanelOpen ? 'Maximize view' : 'Show commands'}
          </button>
        ) : null}
        <button
          type="button"
          onClick={session ? onStop : onStart}
          disabled={isConnecting}
          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
        >
          {session ? 'End Session' : 'Start Browser'}
        </button>
      </div>

      {navigateError ? (
        <div
          className="border-b border-red-200 bg-red-50 px-4 py-1.5 text-xs text-red-800 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-100"
          role="alert"
          data-testid="browser-url-navigate-error"
        >
          {navigateError}
        </div>
      ) : null}

      {signInRequired ? (
        <div
          className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-100"
          role="status"
        >
          Sign in in the browser panel (LinkedIn, Salesforce, etc.), then send your command again to
          continue automation.
        </div>
      ) : null}

      {showEmbedModeToggle ? (
        <div
          className="border-b border-gray-200 bg-gray-50 px-4 py-1.5 text-xs text-gray-600 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300"
          role="status"
          data-testid="browser-embed-mode-banner"
        >
          {embedMode === 'inspector' ? (
            <span>
              Screencast mode — broader browser support.{' '}
              <button
                type="button"
                className="font-medium text-primary-600 hover:underline dark:text-primary-400"
                onClick={() => setEmbedMode('player')}
              >
                Try WebRTC player
              </button>
            </span>
          ) : (
            <span>
              Live view loading slowly?{' '}
              <button
                type="button"
                className="font-medium text-primary-600 hover:underline dark:text-primary-400"
                onClick={() => setEmbedMode('inspector')}
              >
                Use screencast mode
              </button>
            </span>
          )}
        </div>
      ) : null}

      <div className={`relative min-h-0 flex-1 basis-0 max-md:min-h-[50vh] ${ASSISTANT_RAIL_BG_CLASS}`}>
        {isConnecting && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-white/70 dark:bg-gray-900/70">
            <span
              className="h-8 w-8 animate-spin rounded-full border-2 border-primary-500 border-t-transparent"
              aria-hidden
            />
            <span className="text-sm text-gray-600 dark:text-gray-300">
              {provider === 'steel'
                ? 'Starting Steel browser session…'
                : 'Starting remote browser session…'}
            </span>
          </div>
        )}
        {session && !embedLiveViewUrl && !isConnecting ? (
          <div
            className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-white/80 p-6 text-center dark:bg-gray-900/80"
            role="status"
            data-testid="browser-preparing-live-view"
          >
            <span
              className="h-9 w-9 animate-spin rounded-full border-2 border-primary-500 border-t-transparent"
              aria-hidden
            />
            <p className="text-sm font-medium text-gray-800 dark:text-gray-100">
              {provider === 'steel' ? 'Connecting Steel live view…' : 'Preparing live browser view…'}
            </p>
            <p className="max-w-sm text-xs text-gray-500 dark:text-gray-400">
              {preparingLong
                ? provider === 'steel'
                  ? 'Still connecting. Confirm make dev-browser-up is running and STEEL_PUBLIC_URL uses /steel on :5173. Commands work below; use Open in new tab when the link appears.'
                  : 'Still connecting to the remote browser. You can send commands below, or use Open in new tab once the live view link appears.'
                : provider === 'steel'
                  ? 'Low-latency local Chromium — screencast loads in a moment. Commands unlock when the panel is ready.'
                  : 'Connecting to the remote browser. You can send commands as soon as the panel appears.'}
            </p>
          </div>
        ) : null}
        {embedLiveViewUrl ? (
          <iframe
            key={embedLiveViewUrl}
            src={embedLiveViewUrl}
            className="h-full w-full border-0 bg-gray-900"
            allow="clipboard-read; clipboard-write"
            title="AI Browser live view"
            data-testid="browser-live-iframe"
          />
        ) : null}
        {!embedLiveViewUrl && !session ? (
          <BrowserEmptyState
            isConnecting={isConnecting}
            onStart={sessionExpired && onReconnect ? onReconnect : onStart}
            startLabel={sessionExpired ? 'Reconnect browser' : undefined}
            showPrimaryButton={sessionExpired}
            onStartMission={showMobileMissionPicker ? onStartMission : undefined}
            providerLabel={providerLabel}
            providerReady={providerReady}
          />
        ) : null}
      </div>
    </div>
  );
}
